import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Eye, Target } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { api } from "@/lib/api";
import { BUSINESS, IMAGES, VALUES } from "@/data/content";

export default function About() {
    const [bio, setBio] = useState("");
    useEffect(() => {
        api.get("/settings/public").then((r) => setBio(r.data.ceo_bio || "")).catch(() => {});
    }, []);

    return (
        <main className="pt-32 sm:pt-40">
            <Seo title="About Us" path="/about" description="Meet Innovative Fusion Hives — a full-service digital creative agency in Long Beach, CA led by CEO Peter I. Castillo. Our mission, vision, and values." />
            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 pb-20" data-testid="about-hero">
                <Reveal>
                    <Eyebrow>About Us</Eyebrow>
                    <h1 className="mt-4 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
                        The hive behind the work.
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
                        Innovative Fusion Hives is a full-service digital creative agency based in Long Beach, California. We fuse design, technology, and marketing into connected solutions that help businesses stand out and grow.
                    </p>
                </Reveal>
            </section>

            <section className="border-t border-white/5 py-20" data-testid="about-mission-vision">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 grid gap-6 md:grid-cols-2">
                    {[
                        { icon: Target, t: "Our Mission", d: "To help businesses build stronger brands and grow through creative and effective digital solutions." },
                        { icon: Eye, t: "Our Vision", d: "To become a trusted creative and digital growth partner for businesses — the first call when ambition needs execution." },
                    ].map((c, i) => (
                        <Reveal key={c.t} delay={i * 0.1}>
                            <div className="h-full rounded-2xl border border-white/10 bg-hive-card p-8 sm:p-10">
                                <c.icon className="h-8 w-8 text-violet-400" />
                                <h2 className="mt-5 font-display text-2xl font-bold text-white">{c.t}</h2>
                                <p className="mt-3 text-slate-400 leading-relaxed">{c.d}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="border-t border-white/5 py-20 bg-[#0C101A]" data-testid="about-values">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                    <Reveal>
                        <Eyebrow>What We Stand For</Eyebrow>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Values that shape every project.</h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.name} delay={i * 0.06}>
                                <div className="h-full rounded-2xl border border-white/10 bg-hive-card p-6 transition-colors hover:border-amber-500/40">
                                    <p className="font-mono2 text-xs text-amber-400">{String(i + 1).padStart(2, "0")}</p>
                                    <h3 className="mt-3 font-display font-bold text-white">{v.name}</h3>
                                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{v.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-white/5 py-20" data-testid="about-ceo">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-4">
                        <div className="relative mx-auto max-w-xs">
                            <div className="aspect-square rounded-2xl border border-white/10 bg-[radial-gradient(circle_at_50%_35%,rgba(124,58,237,0.25),transparent_70%)] bg-hive-card flex items-center justify-center">
                                <span className="font-display text-7xl font-extrabold bg-gradient-to-br from-violet-400 to-cyan-300 bg-clip-text text-transparent">PC</span>
                            </div>
                            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-amber-500/30 bg-[#14100a] px-4 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-400">
                                Founder & CEO
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-8">
                        <Eyebrow>Leadership</Eyebrow>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">{BUSINESS.ceo}</h2>
                        <p className="mt-2 font-mono2 text-xs uppercase tracking-[0.25em] text-violet-400">Chief Executive Officer</p>
                        {bio ? (
                            <p className="mt-6 text-base text-slate-400 leading-relaxed whitespace-pre-line" data-testid="ceo-bio">{bio}</p>
                        ) : (
                            <p className="mt-6 text-base text-slate-500 leading-relaxed italic" data-testid="ceo-bio-placeholder">
                                A full biography for {BUSINESS.ceo} will be published here soon. What drives the agency today is simple: a belief that small businesses deserve the same caliber of creative and digital thinking as the biggest brands — delivered with honesty, craft, and measurable results.
                            </p>
                        )}
                    </Reveal>
                </div>
            </section>

            <section className="border-t border-white/5 py-20 bg-[#0C101A]" data-testid="about-commitment">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-12 items-center">
                    <Reveal>
                        <Eyebrow>Our Commitment</Eyebrow>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Your goals become the brief.</h2>
                        <p className="mt-5 text-base text-slate-400 leading-relaxed">
                            Every engagement begins with listening. We measure our work against what it does for your business — inquiries, visibility, brand strength — not against awards or vanity metrics. When something is not working, we say so and fix it.
                        </p>
                        <Link to="/contact" data-testid="about-cta-start-project"
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-violet-600 px-7 py-3.5 font-semibold text-white transition-all hover:bg-violet-500 hover:shadow-[0_0_28px_rgba(124,58,237,0.5)]">
                            Start Your Project <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <img src={IMAGES.workspace} alt="Inside the Innovative Fusion Hives creative workspace" className="rounded-2xl border border-white/10 aspect-[4/3] w-full object-cover" loading="lazy" />
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
